import { decode } from 'html-entities';
import removeMarkdown from '../../esm/lib/removeMarkdown.js';
import removeEmoji from './removeEmoji.js';

/**
 * Regex for a GFM table delimiter row, e.g. `| --- | :--: |` or `--- | ---`.
 * A run of pipe-separated dash groups (with optional `:` alignment colons),
 * nothing else on the line.
 */
const TABLE_DELIMITER_ROW = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;

/**
 * Remove GFM markdown tables so their pipes/dashes aren't read aloud by TTS.
 * The old S3 path stripped tables at the DOM level (`createAudioScript.js`);
 * JiT reads raw YAML markdown, so tables must be removed here instead.
 *
 * A table is identified by its delimiter row; we drop that row, the header row
 * immediately above it, and the contiguous body rows below it (lines containing
 * a `|`), stopping at the first blank line. Runs before `removeMarkdown` so the
 * delimiter row is still intact for detection.
 */
function stripMarkdownTables(input) {
  const lines = input.split('\n');
  const drop = new Array(lines.length).fill(false);

  for (let i = 0; i < lines.length; i++) {
    if (!TABLE_DELIMITER_ROW.test(lines[i])) continue;

    // delimiter row
    drop[i] = true;
    // header row: the non-blank line immediately above
    if (i - 1 >= 0 && lines[i - 1].trim() !== '') drop[i - 1] = true;
    // body rows: contiguous following lines that contain a pipe
    for (let j = i + 1; j < lines.length; j++) {
      if (lines[j].trim() === '' || !lines[j].includes('|')) break;
      drop[j] = true;
    }
  }

  return lines.filter((_, i) => !drop[i]).join('\n');
}

/**
 * Inline-link destination `(…)`: any run of non-paren chars, allowing up to two
 * levels of balanced nested parens (covers real URLs like `.../file (public).docx`
 * and `.../doc(v2).pdf`). `removeMarkdown`'s own link regex is non-greedy and
 * stops at the first `)` inside the URL, leaking `.pdf)` fragments into the
 * narration — so we strip links here, before it runs.
 */
const LINK_DEST = '(?:[^()\\n]|\\((?:[^()\\n]|\\([^()\\n]*\\))*\\))*';
const MARKDOWN_IMAGE = new RegExp('!\\[[^\\]]*\\]\\(' + LINK_DEST + '\\)', 'g');
const MARKDOWN_LINK = new RegExp('\\[([^\\]]*)\\]\\(' + LINK_DEST + '\\)', 'g');

/**
 * Remove markdown images and inline links so their URLs aren't read aloud.
 * Mirrors the old S3 DOM path (`createAudioScript.js`), which dropped `<img>`
 * elements entirely and replaced `<a>` elements with just their text. Runs
 * before `removeMarkdown` — images are removed whole (alt text included, matching
 * the old `img` removal); inline links keep their text and drop the URL.
 * Reference-style links (`[text][ref]`) are left for `removeMarkdown` to handle.
 */
function stripMarkdownLinks(input) {
  return input.replace(MARKDOWN_IMAGE, '').replace(MARKDOWN_LINK, '$1');
}

/**
 * Shared markdown / emoji / character cleanup for S3 audio scripts and JiT narration.
 * S3 createAudioScript usually has no ^ delimiters (DOM render → HTML → markdown);
 * JiT reads raw YAML, so ^…^ is stripped here explicitly.
 */
export default function stripNarrationText(input) {
  let text = String(input ?? '');
  text = decode(text);
  text = stripMarkdownTables(text);
  text = stripMarkdownLinks(text);
  text = removeMarkdown(text);
  text = removeEmoji(text);
  text = text.replace(/\u00A0/g, ' ').replace(/\u202f/g, ' ');
  text = text.replace(/\^([^^]*?)\^/g, '$1');
  text = text.replaceAll('*', '').replaceAll('_', '').replaceAll('\\', '');
  return text.trim();
}
